import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { staffApi, errMsg } from "@/lib/api";
import { useContent } from "@/lib/content-store";
import { Card, ContentExtras } from "@/components/staff/ContentExtras";
import { DisplaySettingsCard } from "@/components/staff/DisplaySettingsCard";

const KEYS = ["address", "email", "phone", "services", "ministries", "welcome_new", "welcome_member", "current_series", "past_services", "slides"];

export const ContentEditor = () => {
  const content = useContent();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!f) setF(Object.fromEntries(KEYS.map((k) => [k, content[k]])));
  }, [content, f]);

  if (!f) return null;
  const setMinistry = (i, v) => setF({ ...f, ministries: f.ministries.map((m, j) => (j === i ? v : m)) });

  const save = async () => {
    setBusy(true);
    try {
      await staffApi().put("/content", { ...f, past_services: f.past_services.filter((v) => v.url.trim()) });
      content.reload(); toast.success("Website updated");
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="content-editor">
      <DisplaySettingsCard />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Website pages</h2>
          <p className="text-sm text-slate-500">Changes show on the public website as soon as you save.</p>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="content-save-button">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save changes
        </Button>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Church details" hint="Shown in Plan a Visit, the footer and event directions.">
          <div className="space-y-1.5"><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="content-address-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="content-email-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="content-phone-input" /></div>
        </Card>
        <Card title="Ministries" hint="Listed in the Serve section.">
          {f.ministries.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input value={m} onChange={(e) => setMinistry(i, e.target.value)} data-testid={`content-ministry-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => setF({ ...f, ministries: f.ministries.filter((_, j) => j !== i) })} className="text-rose-500" data-testid={`content-ministry-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setF({ ...f, ministries: [...f.ministries, ""] })} data-testid="content-ministry-add"><Plus className="mr-1 h-4 w-4" /> Add ministry</Button>
        </Card>
        <ContentExtras f={f} setF={setF} content={content} />
      </div>
    </div>
  );
};
